<template>
  <b-modal
    v-model="showModal"
    title="Add Special"
    size="lg"
    class="m-4"
    no-close-on-backdrop
    @hidden="$emit('modal-closed')"
    @ok="submitHandler"
  >
    <b-form @submit.prevent="submitHandler">
      <b-row>
        <b-col cols="12">
          <b-alert
            variant="danger"
            :show="errorMessage ? true : false"
          >
            <div
              v-if="typeof errorMessage === 'string'"
              class="alert-body"
            >
              <p>
                {{ errorMessage }}
              </p>
            </div>
            <div
              v-else-if="errorMessage.detail"
              class="alert-body"
            >
              <p>
                {{ errorMessage.detail }}
              </p>
            </div>
            <div
              v-else-if="errorMessage.non_field_errors"
              class="alert-body"
            >
              <p>
                {{ errorMessage.non_field_errors[0] }}
              </p>
            </div>
            <div
              v-else-if="errorMessage.message"
              class="alert-body"
            >
              <p>
                {{ errorMessage.message }}
              </p>
            </div>
          </b-alert>
        </b-col>

        <!-- Product Selection -->
        <b-col cols="6">
          <b-form-group>
            <template #label>
              Product
              <span class="text-danger">*</span>
            </template>
            <custom-v-select
              v-model="selectedProduct"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="filteredProductOptions || []"
              :loading="productOptionsLoading"
              :class="{ 'error-border': validationErrors.product }"
              label="display_text"
              placeholder="Search product..."
              :filterable="false"
              @search="searchProducts"
              @input="handleProductChange"
              @open="onProductDropdownOpen"
            />
            <p
              v-if="validationErrors.product"
              class="error-message"
            >
              {{ validationErrors.product }}
            </p>
          </b-form-group>
        </b-col>

        <!-- Active Status Switch -->
        <b-col cols="6">
          <b-form-group>
            <template #label>
              Status
            </template>
            <div class="d-flex align-items-center">
              <b-form-checkbox
                v-model="isActive"
                switch
                size="lg"
                @change="handleActiveStatusChange"
              >
                {{ isActive ? 'Active' : 'Inactive' }}
              </b-form-checkbox>
            </div>
          </b-form-group>
        </b-col>

        <!-- Batch Selection -->
        <b-col cols="12">
          <b-form-group>
            <template #label>
              Batches
            </template>
            <custom-v-select
              v-model="selectedBatches"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="batchOptions"
              :loading="batchLoading"
              :class="{ 'error-border': validationErrors.batches }"
              label="text"
              multiple
              placeholder="Select Batches"
              @input="handleBatchChange"
            />
            <p
              v-if="validationErrors.batches"
              class="error-message"
            >
              {{ validationErrors.batches }}
            </p>
            <p
              v-if="batchLoadingError"
              class="error-message"
            >
              {{ batchLoadingError }}
            </p>
          </b-form-group>
        </b-col>

        <!-- Start Date -->
        <b-col cols="6">
          <b-form-group
            label="Start Date"
            label-for="start-date"
          >
            <b-form-datepicker
              id="start-date"
              v-model="startDate"
              menu-class="w-100"
              calendar-width="100%"
              :class="{ 'error-border': validationErrors.startDate }"
              reset-button
              @input="handleStartDateChange"
            />
            <p
              v-if="validationErrors.startDate"
              class="error-message"
            >
              {{ validationErrors.startDate }}
            </p>
            <p
              v-if="errorMessage && errorMessage.start_date"
              class="error-message"
            >
              {{ errorMessage.start_date[0] }}
            </p>
          </b-form-group>
        </b-col>

        <!-- End Date -->
        <b-col cols="6">
          <b-form-group
            label="End Date"
            label-for="end-date"
          >
            <b-form-datepicker
              id="end-date"
              v-model="endDate"
              menu-class="w-100"
              calendar-width="100%"
              :min="startDate"
              :class="{ 'error-border': validationErrors.endDate }"
              reset-button
            />
            <p
              v-if="validationErrors.endDate"
              class="error-message"
            >
              {{ validationErrors.endDate }}
            </p>
            <p
              v-if="errorMessage && errorMessage.end_date"
              class="error-message"
            >
              {{ errorMessage.end_date[0] }}
            </p>
          </b-form-group>
        </b-col>

        <!-- Bonus Type -->
        <b-col cols="12">
          <b-form-group>
            <template #label>
              Bonus Type
            </template>
            <custom-v-select
              v-model="selectedBonusType"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="bonusTypeOptions"
              :class="{ 'error-border': validationErrors.bonusType }"
              label="text"
              placeholder="Select bonus type"
              @input="handleBonusTypeChange"
            />
            <p
              v-if="validationErrors.bonusType"
              class="error-message"
            >
              {{ validationErrors.bonusType }}
            </p>
          </b-form-group>
        </b-col>

        <!-- Net Rate Configuration (for net rate bonus type) -->
        <b-col
          v-if="selectedBonusType && selectedBonusType.value === 'rate'"
          cols="12"
        >
          <h5 class="mt-1 mb-2">
            Net Rate Configuration
          </h5>
          <div
            v-for="(rate, index) in rates"
            :key="index"
          >
            <b-row>
              <b-col
                cols="1"
                class="d-flex align-items-flex-start"
              >
                <span class="h3">{{ index + 1 }}.</span>
              </b-col>
              <b-col cols="5">
                <b-form-group>
                  <b-form-input
                    v-model="rate.min_quantity"
                    type="number"
                    min="0"
                    max="99999"
                    placeholder="Enter minimum quantity (max 5 digits)"
                    :class="{ 'error-border': validationErrors[`min_quantity_${index}`] }"
                    @input="handleQuantityChange(index)"
                  />
                  <p
                    v-if="validationErrors[`min_quantity_${index}`]"
                    class="error-message"
                  >
                    {{ validationErrors[`min_quantity_${index}`] }}
                  </p>
                </b-form-group>
              </b-col>
              <b-col cols="5">
                <b-form-group>
                  <b-form-input
                    v-model="rate.rate"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Enter net rate (max 10 digits)"
                    :class="{ 'error-border': validationErrors[`rate_${index}`] }"
                    @input="handleRateChange(index)"
                  />
                  <p
                    v-if="validationErrors[`rate_${index}`]"
                    class="error-message"
                  >
                    {{ validationErrors[`rate_${index}`] }}
                  </p>
                </b-form-group>
              </b-col>
              <b-col
                cols="1"
                class="d-flex align-items-flex-start"
              >
                <feather-icon
                  v-if="rates.length > 1"
                  title="Delete"
                  icon="TrashIcon"
                  size="25"
                  color="red"
                  class="cursor-pointer"
                  @click="removeRate(index)"
                />
              </b-col>
            </b-row>
          </div>
          <b-button
            v-if="rates.length < 4"
            variant="outline-primary"
            @click="addRate"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
              class="mr-1"
            />
            Add Net Rate
          </b-button>
        </b-col>

        <!-- Lot Configuration (for lot bonus type) -->
        <b-col
          v-if="selectedBonusType && selectedBonusType.value === 'lot'"
          cols="12"
        >
          <h5 class="mt-1 mb-2">
            Lot Configuration
          </h5>
          <div
            v-for="(lot, index) in lots"
            :key="index"
          >
            <b-row>
              <b-col
                cols="1"
                class="d-flex align-items-flex-start"
              >
                <span class="h3">{{ index + 1 }}.</span>
              </b-col>
              <b-col cols="4">
                <b-form-group>
                  <b-form-input
                    v-model="lot.qty"
                    type="number"
                    min="0"
                    max="99999"
                    placeholder="Enter quantity (max 5 digits)"
                    :class="{ 'error-border': validationErrors[`lot_qty_${index}`] }"
                    @input="handleLotQtyChange(index)"
                  />
                  <p
                    v-if="validationErrors[`lot_qty_${index}`]"
                    class="error-message"
                  >
                    {{ validationErrors[`lot_qty_${index}`] }}
                  </p>
                </b-form-group>
              </b-col>
              <b-col cols="1">
                <span class="h1">+</span>
              </b-col>
              <b-col cols="5">
                <b-form-group>
                  <b-form-input
                    v-model="lot.free_qty"
                    type="number"
                    min="0"
                    max="99999"
                    placeholder="Enter free quantity (max 5 digits)"
                    :class="{ 'error-border': validationErrors[`lot_free_qty_${index}`] }"
                    @input="handleLotFreeQtyChange(index)"
                  />
                  <p
                    v-if="validationErrors[`lot_free_qty_${index}`]"
                    class="error-message"
                  >
                    {{ validationErrors[`lot_free_qty_${index}`] }}
                  </p>
                </b-form-group>
              </b-col>
              <b-col
                cols="1"
                class="d-flex align-items-flex-start"
              >
                <feather-icon
                  v-if="lots.length > 1"
                  title="Delete"
                  icon="TrashIcon"
                  size="25"
                  color="red"
                  class="cursor-pointer"
                  @click="removeLot(index)"
                />
              </b-col>
            </b-row>
          </div>
          <b-button
            v-if="lots.length < 4"
            variant="outline-primary"
            @click="addLot"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
              class="mr-1"
            />
            Add Lot
          </b-button>
        </b-col>

        <!-- Gift Configuration (for gift bonus type) -->
        <b-col
          v-if="selectedBonusType && selectedBonusType.value === 'gift'"
          cols="12"
        >
          <h5 class="mt-1 mb-2">
            Gift Configuration
          </h5>

          <!-- Gift Name -->
          <b-row>
            <b-col cols="6">
              <b-form-group>
                <template #label>
                  Gift Name
                  <span class="text-danger">*</span>
                </template>
                <b-form-input
                  v-model="giftConfig.name"
                  type="text"
                  placeholder="Enter gift name (max 20 character)"
                  :class="{ 'error-border': validationErrors.giftName }"
                  @input="handleGiftNameChange"
                />
                <p
                  v-if="validationErrors.giftName"
                  class="error-message"
                >
                  {{ validationErrors.giftName }}
                </p>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group>
                <template #label>
                  Eligibility Type
                  <span class="text-danger">*</span>
                </template>
                <custom-v-select
                  v-model="selectedEligibilityType"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="eligibilityTypeOptions"
                  :class="{ 'error-border': validationErrors.eligibilityType }"
                  label="text"
                  placeholder="Select eligibility type"
                  @input="handleEligibilityTypeChange"
                />
                <p
                  v-if="validationErrors.eligibilityType"
                  class="error-message"
                >
                  {{ validationErrors.eligibilityType }}
                </p>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Gift Description -->
          <b-row>
            <b-col cols="12">
              <b-form-group label="Description">
                <b-form-textarea
                  v-model="giftConfig.description"
                  rows="3"
                  placeholder="Enter gift description (max 75 character)"
                  :class="{ 'error-border': validationErrors.giftDescription }"
                />
                <p
                  v-if="validationErrors.giftDescription"
                  class="error-message"
                >
                  {{ validationErrors.giftDescription }}
                </p>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Min Amount (for amount eligibility) -->
          <b-row v-if="selectedEligibilityType && selectedEligibilityType.value === 'amount'">
            <b-col cols="6">
              <b-form-group>
                <template #label>
                  Minimum Amount
                  <span class="text-danger">*</span>
                </template>
                <b-form-input
                  v-model="giftConfig.min_amount"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Enter minimum amount (max 10 digits)"
                  :class="{ 'error-border': validationErrors.minAmount }"
                  @input="handleGiftAmountChange"
                />
                <p
                  v-if="validationErrors.minAmount"
                  class="error-message"
                >
                  {{ validationErrors.minAmount }}
                </p>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Min Quantity (for quantity eligibility) -->
          <div v-if="selectedEligibilityType && selectedEligibilityType.value === 'quantity'">
            <h6 class="mt-2 mb-2">
              Minimum Quantities
            </h6>
            <div
              v-for="(qty, index) in giftQuantities"
              :key="index"
            >
              <b-row>
                <b-col
                  cols="1"
                  class="d-flex align-items-flex-start"
                >
                  <span class="h6">{{ index + 1 }}.</span>
                </b-col>
                <b-col cols="10">
                  <b-form-group>
                    <b-form-input
                      v-model="qty.value"
                      type="number"
                      min="0"
                      max="999999"
                      placeholder="Enter minimum quantity (max 6 digits)"
                      :class="{ 'error-border': validationErrors[`minQuantity_${index}`] }"
                      @input="handleGiftQuantityChange(index)"
                    />
                    <p
                      v-if="validationErrors[`minQuantity_${index}`]"
                      class="error-message"
                    >
                      {{ validationErrors[`minQuantity_${index}`] }}
                    </p>
                  </b-form-group>
                </b-col>
                <b-col
                  cols="1"
                  class="d-flex align-items-flex-start"
                >
                  <feather-icon
                    v-if="giftQuantities && giftQuantities.length > 1"
                    title="Delete"
                    icon="TrashIcon"
                    size="20"
                    color="red"
                    class="cursor-pointer"
                    @click="removeGiftQuantity(index)"
                  />
                </b-col>
              </b-row>
            </div>
            <b-button
              v-if="giftQuantities.length < 4"
              variant="outline-secondary"
              size="sm"
              class="mt-2"
              @click="addGiftQuantity"
            >
              <feather-icon
                icon="PlusIcon"
                size="14"
                class="mr-1"
              />
              Add Quantity
            </b-button>
          </div>

          <!-- Gift Images -->
          <div>
            <h6 class="mt-3 mb-2">
              Gift Images
            </h6>
            <div
              v-for="(image, index) in giftImages"
              :key="index"
            >
              <b-row>
                <b-col
                  cols="1"
                  class="d-flex align-items-flex-start"
                >
                  <span class="h6">{{ index + 1 }}.</span>
                </b-col>
                <b-col cols="10">
                  <b-form-group>
                    <b-form-file
                      v-model="image.file"
                      accept=".pdf,.jpg,.png,.jpeg"
                      placeholder="Choose gift image..."
                      :class="{ 'error-border': validationErrors[`giftImage_${index}`] }"
                    />
                    <p
                      v-if="validationErrors[`giftImage_${index}`]"
                      class="error-message"
                    >
                      {{ validationErrors[`giftImage_${index}`] }}
                    </p>
                  </b-form-group>
                </b-col>
                <b-col
                  cols="1"
                  class="d-flex align-items-flex-start"
                >
                  <feather-icon
                    v-if="giftImages.length > 1"
                    title="Delete"
                    icon="TrashIcon"
                    size="20"
                    color="red"
                    class="cursor-pointer"
                    @click="removeGiftImage(index)"
                  />
                </b-col>
              </b-row>
            </div>
            <b-button
              v-if="giftImages.length < 4"
              variant="outline-secondary"
              size="sm"
              class="mt-2"
              @click="addGiftImage"
            >
              <feather-icon
                icon="PlusIcon"
                size="14"
                class="mr-1"
              />
              Add Image
            </b-button>
          </div>
        </b-col>
        <!-- Free Product Configuration (for free_product bonus type) -->
        <b-col
          v-if="selectedBonusType && selectedBonusType.value === 'free_product'"
          cols="12"
        >
          <h5 class="mt-1 mb-2">
            Free Product Configuration
          </h5>

          <b-row>
            <!-- Free Product Selection -->
            <b-col cols="12">
              <b-form-group>
                <template #label>
                  Free Product
                  <span class="text-danger">*</span>
                </template>
                <custom-v-select
                  v-model="freeProductConfig.free_product_code"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="filteredFreeProductOptions || []"
                  :loading="productOptionsLoading"
                  :class="{ 'error-border': validationErrors.freeProductCode }"
                  label="display_text"
                  placeholder="Search free product..."
                  :filterable="false"
                  @search="searchFreeProducts"
                  @input="handleFreeProductChange"
                  @open="onFreeProductDropdownOpen"
                />
                <p
                  v-if="validationErrors.freeProductCode"
                  class="error-message"
                >
                  {{ validationErrors.freeProductCode }}
                </p>
              </b-form-group>
            </b-col>

            <!-- Main Product Quantity -->
            <b-col cols="6">
              <b-form-group>
                <template #label>
                  Main Product Quantity
                  <span class="text-danger">*</span>
                </template>
                <b-form-input
                  v-model="freeProductConfig.main_product_quantity"
                  type="number"
                  min="1"
                  max="99999"
                  placeholder="Enter main product quantity (max 5 digits)"
                  :class="{ 'error-border': validationErrors.mainProductQuantity }"
                  @input="handleMainProductQuantityChange"
                />
                <p
                  v-if="validationErrors.mainProductQuantity"
                  class="error-message"
                >
                  {{ validationErrors.mainProductQuantity }}
                </p>
              </b-form-group>
            </b-col>

            <!-- Free Product Quantity -->
            <b-col cols="6">
              <b-form-group>
                <template #label>
                  Free Product Quantity
                  <span class="text-danger">*</span>
                </template>
                <b-form-input
                  v-model="freeProductConfig.free_product_quantity"
                  type="number"
                  min="1"
                  max="99999"
                  placeholder="Enter free product quantity (max 5 digits)"
                  :class="{ 'error-border': validationErrors.freeProductQuantity }"
                  @input="handleFreeProductQuantityChange"
                />
                <p
                  v-if="validationErrors.freeProductQuantity"
                  class="error-message"
                >
                  {{ validationErrors.freeProductQuantity }}
                </p>
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-form>

    <template #modal-footer="{ ok, cancel }">
      <b-button
        variant="secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="saving"
        @click="ok()"
      >
        Create Special
        <b-spinner
          v-if="saving"
          small
          label="Small Spinner"
        />
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BRow, BCol, BFormGroup, BButton, BFormInput,
  BForm, BSpinner, BAlert, BFormTextarea, BFormFile, BFormDatepicker,
  BFormCheckbox,
} from 'bootstrap-vue'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import getParsedQueryString from '@/utils/headerQueryParser'

export default {
  components: {
    CustomVSelect,
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BButton,
    BSpinner,
    BAlert,
    BFormTextarea,
    BFormFile,
    BFormDatepicker,
    BFormCheckbox,
  },

  data() {
    return {
      showModal: true,
      selectedProduct: null,
      productOptions: [], // Global product options loaded once
      filteredProductOptions: [], // Filtered options for main product
      filteredFreeProductOptions: [], // Filtered options for free product
      productOptionsLoading: false,
      isActive: true,
      selectedBatches: [],
      batchOptions: [],
      batchLoading: false,
      batchLoadingError: null,
      startDate: null,
      endDate: null,
      selectedBonusType: { value: 'rate', text: 'Net Rate' },
      bonusTypeOptions: [
        { value: 'rate', text: 'Net Rate' },
        { value: 'lot', text: 'Lot' },
        { value: 'gift', text: 'Gift' },
        { value: 'free_product', text: 'Free Product' },
      ],
      rates: [
        { rate: '', min_quantity: '' },
      ],
      lots: [
        { qty: '', free_qty: '' },
      ],
      giftConfig: {
        name: '',
        description: '',
        min_amount: '',
      },
      giftQuantities: [{ value: '' }],
      selectedEligibilityType: { value: 'quantity', text: 'Quantity' },
      eligibilityTypeOptions: [
        { value: 'amount', text: 'Amount' },
        { value: 'quantity', text: 'Quantity' },
      ],
      giftImages: [
        { file: null },
      ],
      freeProductConfig: {
        free_product_code: null,
        free_product_quantity: '',
        main_product_quantity: '',
      },
      saving: false,
      errorMessage: {},
      validationErrors: {},
      searchTimeout: null,
    }
  },

  created() {
    this.fetchProductOptions()
  },

  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },

  methods: {
    async fetchProductOptions() {
      this.productOptionsLoading = true
      try {
        const productRes = await axios.get('/marg/product_options')
        this.productOptions = productRes.data.data.map(product => ({
          id: product.code,
          code: product.code,
          name: product.name,
          display_text: `${product.name}`,
          rate: parseFloat(product.rate),
          mrp: parseFloat(product.mrp),
        }))

        // Initialize with first 15 products for better performance
        this.filteredProductOptions = this.productOptions.slice(0, 15)
        this.filteredFreeProductOptions = this.productOptions.slice(0, 15)

        this.productOptionsLoading = false
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || 'Failed to load products'
        this.productOptionsLoading = false
      }
    },

    // Handle main product dropdown open - show initial products immediately
    onProductDropdownOpen() {
      if (!this.filteredProductOptions.length && this.productOptions.length) {
        this.filteredProductOptions = this.productOptions.slice(0, 15)
      }
    },

    // Handle free product dropdown open - show initial products immediately
    onFreeProductDropdownOpen() {
      if (!this.filteredFreeProductOptions.length && this.productOptions.length) {
        this.filteredFreeProductOptions = this.productOptions.slice(0, 15)
      }
    },

    // Client-side product search for main product
    searchProducts(query) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      this.searchTimeout = setTimeout(() => {
        if (!query || query.trim() === '') {
          // Show first 15 products when query is empty
          this.filteredProductOptions = this.productOptions.slice(0, 15)
        } else {
          // Filter products by name (case-insensitive)
          const searchTerm = query.toLowerCase()
          const filtered = this.productOptions.filter(product => product.name.toLowerCase().includes(searchTerm))
          // Limit to 50 results for performance
          this.filteredProductOptions = filtered.slice(0, 50)
        }
      }, 200) // Reduced to 200ms for faster response
    },

    // Client-side product search for free product
    searchFreeProducts(query) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      this.searchTimeout = setTimeout(() => {
        if (!query || query.trim() === '') {
          // Show first 15 products when query is empty
          this.filteredFreeProductOptions = this.productOptions.slice(0, 15)
        } else {
          // Filter products by name (case-insensitive)
          const searchTerm = query.toLowerCase()
          const filtered = this.productOptions.filter(product => product.name.toLowerCase().includes(searchTerm))
          // Limit to 50 results for performance
          this.filteredFreeProductOptions = filtered.slice(0, 50)
        }
      }, 200) // Reduced to 200ms for faster response
    },

    validateForm() {
      this.validationErrors = {}
      let isValid = true

      if (!this.selectedProduct) {
        this.validationErrors.product = 'Product is required'
        isValid = false
      }

      if (!(this.selectedBatches.length > 0 || this.startDate)) {
        this.validationErrors.batches = 'Atleast One batch or Start Date is Required'
        this.validationErrors.startDate = 'Start Date or Atleast One batch is Required'
        isValid = false
      }

      if (this.selectedBonusType) {
        if (this.selectedBonusType.value === 'rate') {
          isValid = this.validateRates() && isValid
        } else if (this.selectedBonusType.value === 'lot') {
          isValid = this.validateLots() && isValid
        } else if (this.selectedBonusType.value === 'gift') {
          isValid = this.validateGift() && isValid
        } else if (this.selectedBonusType.value === 'free_product') {
          isValid = this.validateFreeProduct() && isValid
        }
      }

      return isValid
    },

    validateRates() {
      let isValid = true
      const usedQuantities = new Set()
      let hasValidRate = false
      let hasAnyInput = false
      let hasFieldErrors = false

      this.rates.forEach((rate, index) => {
        const rateValue = rate.rate && rate.rate.toString().trim()
        const quantityValue = rate.min_quantity && rate.min_quantity.toString().trim()

        if (rateValue || quantityValue) {
          hasAnyInput = true
        }

        if ((rateValue && !quantityValue) || (!rateValue && quantityValue)) {
          hasFieldErrors = true
          if (!rateValue) {
            this.validationErrors[`rate_${index}`] = 'Net rate is required when minimum quantity is provided'
            isValid = false
          }
          if (!quantityValue) {
            this.validationErrors[`min_quantity_${index}`] = 'Minimum quantity is required when net rate is provided'
            isValid = false
          }
        }

        if (quantityValue) {
          if (!/^\d{1,5}$/.test(quantityValue)) {
            hasFieldErrors = true
            this.validationErrors[`min_quantity_${index}`] = 'Minimum quantity must be an integer with maximum 5 digits'
            isValid = false
          }
        }

        if (rateValue) {
          const digitCount = rateValue.replace('.', '').length
          if (!/^\d+(\.\d+)?$/.test(rateValue) || digitCount > 10) {
            hasFieldErrors = true
            this.validationErrors[`rate_${index}`] = 'Net rate must be a number with maximum 10 digits (including decimal)'
            isValid = false
          }
        }

        if (rateValue && quantityValue && !this.validationErrors[`min_quantity_${index}`] && !this.validationErrors[`rate_${index}`]) {
          hasValidRate = true
          const quantity = parseInt(quantityValue, 10)
          const rateNum = parseFloat(rateValue)

          if (usedQuantities.has(quantity)) {
            hasFieldErrors = true
            this.validationErrors[`min_quantity_${index}`] = 'Minimum quantity should be different from other slabs'
            isValid = false
          } else {
            usedQuantities.add(quantity)
          }

          if (rateNum <= 0) {
            hasFieldErrors = true
            this.validationErrors[`rate_${index}`] = 'Net rate must be greater than 0'
            isValid = false
          }

          if (quantity <= 0) {
            hasFieldErrors = true
            this.validationErrors[`min_quantity_${index}`] = 'Minimum quantity must be greater than 0'
            isValid = false
          }
        }
      })

      if (!hasValidRate && !hasFieldErrors && !hasAnyInput) {
        this.validationErrors.rate_0 = 'At least one net rate configuration is required'
        this.validationErrors.min_quantity_0 = 'At least one net rate configuration is required'
        isValid = false
      }

      return isValid
    },

    validateLots() {
      let isValid = true
      const usedQuantities = new Set()
      let hasValidLot = false
      let hasAnyInput = false
      let hasFieldErrors = false

      this.lots.forEach((lot, index) => {
        const qtyValue = lot.qty && lot.qty.toString().trim()
        const freeQtyValue = lot.free_qty && lot.free_qty.toString().trim()

        if (qtyValue || freeQtyValue) {
          hasAnyInput = true
        }

        if ((qtyValue && !freeQtyValue) || (!qtyValue && freeQtyValue)) {
          hasFieldErrors = true
          if (!qtyValue) {
            this.validationErrors[`lot_qty_${index}`] = 'Quantity is required when free quantity is provided'
            isValid = false
          }
          if (!freeQtyValue) {
            this.validationErrors[`lot_free_qty_${index}`] = 'Free quantity is required when quantity is provided'
            isValid = false
          }
        }

        if (qtyValue) {
          if (!/^\d{1,5}$/.test(qtyValue)) {
            hasFieldErrors = true
            this.validationErrors[`lot_qty_${index}`] = 'Quantity must be an integer with maximum 5 digits'
            isValid = false
          }
        }

        if (freeQtyValue) {
          if (!/^\d{1,5}$/.test(freeQtyValue)) {
            hasFieldErrors = true
            this.validationErrors[`lot_free_qty_${index}`] = 'Free quantity must be an integer with maximum 5 digits'
            isValid = false
          }
        }

        if (qtyValue && freeQtyValue && !this.validationErrors[`lot_qty_${index}`] && !this.validationErrors[`lot_free_qty_${index}`]) {
          hasValidLot = true
          const quantity = parseInt(qtyValue, 10)
          const freeQuantity = parseInt(freeQtyValue, 10)

          if (usedQuantities.has(quantity)) {
            hasFieldErrors = true
            this.validationErrors[`lot_qty_${index}`] = 'Quantity should be different from other lots'
            isValid = false
          } else {
            usedQuantities.add(quantity)
          }

          if (quantity <= 0) {
            hasFieldErrors = true
            this.validationErrors[`lot_qty_${index}`] = 'Quantity must be greater than 0'
            isValid = false
          }

          if (freeQuantity <= 0) {
            hasFieldErrors = true
            this.validationErrors[`lot_free_qty_${index}`] = 'Free quantity must be greater than 0'
            isValid = false
          }
        }
      })

      if (!hasValidLot && !hasFieldErrors && !hasAnyInput) {
        this.validationErrors.lot_qty_0 = 'At least one lot configuration is required'
        this.validationErrors.lot_free_qty_0 = 'At least one lot configuration is required'
        isValid = false
      }

      return isValid
    },
    validateGift() {
      let isValid = true

      if (!this.giftConfig.name || !this.giftConfig.name.trim()) {
        this.validationErrors.giftName = 'Gift name is required'
        isValid = false
      } else if (this.giftConfig.name.length > 20) {
        this.validationErrors.giftName = 'Gift name must be 20 characters or less'
        isValid = false
      }

      if (this.giftConfig.description.length > 75) {
        this.validationErrors.giftDescription = 'Gift description must be 75 characters or less'
        isValid = false
      }

      if (this.selectedEligibilityType) {
        if (this.selectedEligibilityType.value === 'quantity') {
          let hasValidQuantity = false
          let hasAnyInput = false
          let hasFieldErrors = false
          const usedQuantities = new Set()

          this.giftQuantities.forEach((qty, index) => {
            const quantityValue = qty.value && qty.value.toString().trim()

            if (quantityValue) {
              hasAnyInput = true

              if (!/^\d{1,6}$/.test(quantityValue)) {
                hasFieldErrors = true
                this.validationErrors[`minQuantity_${index}`] = 'Minimum quantity must be an integer with maximum 6 digits'
                isValid = false
              } else {
                const quantity = parseInt(quantityValue, 10)

                if (quantity <= 0) {
                  hasFieldErrors = true
                  this.validationErrors[`minQuantity_${index}`] = 'Minimum quantity must be greater than 0'
                  isValid = false
                } else if (usedQuantities.has(quantity)) {
                  hasFieldErrors = true
                  this.validationErrors[`minQuantity_${index}`] = 'Quantity should be different from other quantities'
                  isValid = false
                } else {
                  usedQuantities.add(quantity)
                  hasValidQuantity = true
                }
              }
            }
          })

          if (!hasValidQuantity && !hasFieldErrors && !hasAnyInput) {
            this.validationErrors.minQuantity_0 = 'At least one minimum quantity is required'
            isValid = false
          }
        } else if (this.selectedEligibilityType.value === 'amount') {
          if (!this.giftConfig.min_amount || this.giftConfig.min_amount.toString().trim() === '') {
            this.validationErrors.minAmount = 'Minimum amount is required'
            isValid = false
          } else {
            const amountValue = this.giftConfig.min_amount.toString().trim()
            const digitCount = amountValue.replace('.', '').length

            if (!/^\d+(\.\d+)?$/.test(amountValue) || digitCount > 10) {
              this.validationErrors.minAmount = 'Minimum amount must be a number with maximum 10 digits (including decimal)'
              isValid = false
            } else {
              const amount = parseFloat(amountValue)
              if (amount <= 0) {
                this.validationErrors.minAmount = 'Minimum amount must be greater than 0'
                isValid = false
              }
            }
          }
        }
      } else {
        this.validationErrors.eligibilityType = 'Eligibility type is required'
        isValid = false
      }

      return isValid
    },
    validateFreeProduct() {
      let isValid = true

      // Validate free product code
      if (!this.freeProductConfig.free_product_code) {
        this.validationErrors.freeProductCode = 'Free product is required'
        isValid = false
      }

      // Validate main product quantity
      if (!this.freeProductConfig.main_product_quantity
      || this.freeProductConfig.main_product_quantity.toString().trim() === '') {
        this.validationErrors.mainProductQuantity = 'Main product quantity is required'
        isValid = false
      } else {
        const mainQty = this.freeProductConfig.main_product_quantity.toString().trim()
        if (!/^\d{1,5}$/.test(mainQty)) {
          this.validationErrors.mainProductQuantity = 'Main product quantity must be an integer with maximum 5 digits'
          isValid = false
        } else {
          const quantity = parseInt(mainQty, 10)
          if (quantity <= 0) {
            this.validationErrors.mainProductQuantity = 'Main product quantity must be greater than 0'
            isValid = false
          }
        }
      }

      // Validate free product quantity
      if (!this.freeProductConfig.free_product_quantity
      || this.freeProductConfig.free_product_quantity.toString().trim() === '') {
        this.validationErrors.freeProductQuantity = 'Free product quantity is required'
        isValid = false
      } else {
        const freeQty = this.freeProductConfig.free_product_quantity.toString().trim()
        if (!/^\d{1,5}$/.test(freeQty)) {
          this.validationErrors.freeProductQuantity = 'Free product quantity must be an integer with maximum 5 digits'
          isValid = false
        } else {
          const quantity = parseInt(freeQty, 10)
          if (quantity <= 0) {
            this.validationErrors.freeProductQuantity = 'Free product quantity must be greater than 0'
            isValid = false
          }
        }
      }

      return isValid
    },
    clearValidationError(field) {
      if (this.validationErrors[field]) {
        this.$delete(this.validationErrors, field)
      }
    },

    handleRateChange(index) {
      this.clearValidationError(`rate_${index}`)
      this.clearValidationError(`min_quantity_${index}`)
    },

    handleQuantityChange(index) {
      this.clearValidationError(`rate_${index}`)
      this.clearValidationError(`min_quantity_${index}`)
    },

    handleLotQtyChange(index) {
      this.clearValidationError(`lot_qty_${index}`)
      this.clearValidationError(`lot_free_qty_${index}`)
    },

    handleLotFreeQtyChange(index) {
      this.clearValidationError(`lot_qty_${index}`)
      this.clearValidationError(`lot_free_qty_${index}`)
    },
    handleStartDateChange() {
      this.clearValidationError('startDate')
      this.clearValidationError('batches')
    },
    handleGiftNameChange() {
      this.clearValidationError('giftName')
    },
    // Method to add a new gift quantity field
    addGiftQuantity() {
      if (this.giftQuantities.length < 4) {
        this.giftQuantities.push({ value: '' })
      }
    },

    // Method to remove a gift quantity field at a specific index
    removeGiftQuantity(index) {
      if (this.giftQuantities.length > 1) {
        this.giftQuantities.splice(index, 1)
        this.clearValidationError(`minQuantity_${index}`)
      }
    },
    handleGiftQuantityChange(index) {
      this.clearValidationError(`minQuantity_${index}`)
    },
    handleGiftAmountChange() {
      this.clearValidationError('minAmount')
    },
    handleActiveStatusChange() {
      this.clearValidationError('isActive')
    },

    handleFreeProductChange() {
      this.clearValidationError('freeProductCode')
    },

    handleMainProductQuantityChange() {
      this.clearValidationError('mainProductQuantity')
    },

    handleFreeProductQuantityChange() {
      this.clearValidationError('freeProductQuantity')
    },

    handleProductChange(product) {
      if (this.validationErrors.product) {
        this.$delete(this.validationErrors, 'product')
      }

      this.selectedBatches = []
      this.batchOptions = []
      this.batchLoadingError = null

      if (product && product.code) {
        this.fetchBatchOptions(product.code)
      }
    },

    async fetchBatchOptions(productCode) {
      const params = { code: productCode }

      this.batchLoading = true
      this.batchLoadingError = null

      try {
        const res = await axios.get(`/marg/get_erp_batches?${getParsedQueryString(params)}`)
        const batchOptions = res.data.batches.reverse().map(batch => ({
          id: batch.id,
          text: batch.batch,
          batch_number: batch.batch_number,
          expiry_date: batch.expiry_date,
          special_bonus: batch.special_bonus,
          disabled: !!batch.special_bonus,
        }))
        this.batchOptions = batchOptions
        this.batchLoading = false
      } catch (error) {
        this.batchLoadingError = error?.response?.data?.error || 'Something went wrong'
        this.batchLoading = false
      }
    },

    handleBatchChange() {
      if (this.validationErrors.batches) {
        this.$delete(this.validationErrors, 'batches')
      }
      if (this.validationErrors.startDate) {
        this.$delete(this.validationErrors, 'startDate')
      }
    },

    handleBonusTypeChange() {
      if (this.validationErrors.bonusType) {
        this.$delete(this.validationErrors, 'bonusType')
      }

      this.rates = [{ rate: '', min_quantity: '' }]
      this.lots = [{ qty: '', free_qty: '' }]
      this.giftConfig = {
        name: '',
        description: '',
        min_amount: '',
      }
      this.freeProductConfig = {
        free_product_code: null,
        free_product_quantity: '',
        main_product_quantity: '',
      }
      this.selectedEligibilityType = { value: 'quantity', text: 'Quantity' }
      this.giftImages = [{ file: null }]
    },

    handleEligibilityTypeChange() {
      if (this.validationErrors.eligibilityType) {
        this.$delete(this.validationErrors, 'eligibilityType')
      }

      this.giftConfig.min_amount = ''
      this.giftConfig.min_quantity = ''
    },

    addRate() {
      if (this.rates.length < 4) {
        this.rates.push({ rate: '', min_quantity: '' })
      }
    },

    removeRate(index) {
      this.rates.splice(index, 1)
      this.clearValidationError(`rate_${index}`)
      this.clearValidationError(`min_quantity_${index}`)
    },

    addLot() {
      if (this.lots.length < 4) {
        this.lots.push({ qty: '', free_qty: '' })
      }
    },

    removeLot(index) {
      this.lots.splice(index, 1)
      this.clearValidationError(`lot_qty_${index}`)
      this.clearValidationError(`lot_free_qty_${index}`)
    },

    addGiftImage() {
      if (this.giftImages.length < 4) {
        this.giftImages.push({ file: null })
      }
    },

    removeGiftImage(index) {
      this.giftImages.splice(index, 1)
      this.clearValidationError(`giftImage_${index}`)
    },

    async submitHandler(event) {
      event.preventDefault()

      if (!this.validateForm()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please fix the errors and try again',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      this.saving = true

      try {
        const formData = new FormData()

        const payload = {
          code: this.selectedProduct.code,
          bonus_type: this.selectedBonusType.value,
          is_active: this.isActive,
        }

        if (this.selectedBatches.length > 0) {
          payload.erp_product_ids = this.selectedBatches.map(batch => batch.id)
        }

        if (this.startDate) {
          payload.start_date = this.startDate
        }

        if (this.endDate) {
          payload.end_date = this.endDate
        }

        if (this.selectedBonusType.value === 'rate') {
          payload.rates = this.rates.filter(rate => rate.rate && rate.min_quantity)
        } else if (this.selectedBonusType.value === 'lot') {
          payload.lots = this.lots
            .filter(lot => lot.qty && lot.free_qty)
            .map(lot => ({
              lot: `${parseInt(lot.qty, 10)}+${parseInt(lot.free_qty, 10)}`,
            }))
        } else if (this.selectedBonusType.value === 'gift') {
          const giftData = {
            name: this.giftConfig.name,
            description: this.giftConfig.description,
            eligibility_type: this.selectedEligibilityType.value,
          }

          if (this.selectedEligibilityType.value === 'quantity') {
            // Add quantities as separate fields: min_quantity, min_quantity_2, min_quantity_3, min_quantity_4
            this.giftQuantities.forEach((qty, index) => {
              const quantityValue = qty.value && qty.value.toString().trim()
              const fieldName = index === 0 ? 'min_quantity' : `min_quantity_${index + 1}`

              if (quantityValue) {
                giftData[fieldName] = parseInt(quantityValue, 10)
              } else {
                giftData[fieldName] = null
              }
            })

            // Fill remaining fields with null if less than 4 quantities
            for (let i = this.giftQuantities.length; i < 4; i += 1) {
              const fieldName = i === 0 ? 'min_quantity' : `min_quantity_${i + 1}`
              giftData[fieldName] = null
            }
          } else if (this.selectedEligibilityType.value === 'amount') {
            giftData.min_amount = parseFloat(this.giftConfig.min_amount) || 0
          }

          payload.gift = giftData
        } else if (this.selectedBonusType.value === 'free_product') {
          payload.free_product = {
            free_product_code: this.freeProductConfig.free_product_code.code,
            free_product_quantity: parseInt(this.freeProductConfig.free_product_quantity, 10).toString(),
            main_product_quantity: parseInt(this.freeProductConfig.main_product_quantity, 10).toString(),
          }
        }

        Object.keys(payload).forEach(key => {
          if (typeof payload[key] === 'object' && payload[key] !== null) {
            formData.append(key, JSON.stringify(payload[key]))
          } else {
            formData.append(key, payload[key])
          }
        })

        if (this.selectedBonusType.value === 'gift' && this.giftImages.length > 0) {
          this.giftImages.forEach((imageObj, index) => {
            if (imageObj.file) {
              formData.append(`image${index + 1}`, imageObj.file)
            }
          })
        }

        await axios.post('/marg/special-bonus/create/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Accept: 'application/json',
          },
        })

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Special Created Successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.$emit('bonus-added')
        this.saving = false
      } catch (error) {
        this.errorMessage = error?.response?.data || 'Something went wrong'
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.error-message {
  color: red;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.error-border {
  border-color: #dc3545 !important;
}

.error-border:focus {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.text-danger {
  color: #dc3545 !important;
}

.cursor-pointer {
  cursor: pointer;
}

input::placeholder,
textarea::placeholder {
  color: #6c757d !important;
  opacity: 1;
}

.vs__dropdown-toggle .vs__selected-options .vs__search::placeholder {
  color: #6c757d !important;
}

.vs__dropdown-menu .vs__dropdown-option--disabled {
  background-color: #ffebee !important;
  color: #c62828 !important;
  cursor: not-allowed !important;
}

.vs__dropdown-menu .vs__dropdown-option--disabled:hover {
  background-color: #ffcdd2 !important;
  color: #b71c1c !important;
}
</style>
